import React, { useState } from 'react'
import { connect } from 'react-redux'
import styled from '@emotion/styled'
import { NegotiationFieldMeta, TradingEntityMetaDetail, WorkflowActions } from 'api'
import { Locale, Translation } from 'locale'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { DialogField, TextInputField } from 'view/components/inputs'
import { ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { PrimeBrokerSelectboxField } from 'view/shared/entity'
import { getReferenceDataEntitiesForTradingEntityDefaultsAmend } from './utils'
import { notEmpty } from 'utils'

interface SalesCoverageDialogProps extends ModalConsumerProps {
  tradingEntityMetaDetails: TradingEntityMetaDetail[]
  onUpdateBulk: typeof WorkflowActions.createEntitiesCreateAndSubmitReferenceData
  onClose?: () => unknown
}

const mapDispatchToProps = {
  onUpdateBulk: WorkflowActions.createEntitiesCreateAndSubmitReferenceData
}

const Content = styled(ModalContent)`
  flex-direction: row;
`

export const TradingEntityDefaultsDialogComponent: React.FunctionComponent<SalesCoverageDialogProps> = props => {
  const { tradingEntityMetaDetails, onUpdateBulk, onClose, showDialog } = props

  const handleCancel = () => {
    onClose?.()
  }

  const hideAliasField =
    new Set(tradingEntityMetaDetails.map(tradingEntityMetaDetail => tradingEntityMetaDetail.ownerTradingEntity)).size !==
    tradingEntityMetaDetails.length

  const [alias, setAlias] = useState<string | null | undefined>(hideAliasField ? undefined : null)
  const [primeBrokerId, setPrimeBrokerId] = useState<string | null>(null)

  const handleApply = async () => {
    const dialogArgs = {
      type: NotificationType.Confirm,
      title: Translation.TC_BULK_APPLY_TRADING_ENTITY_DEFAULTS,
      message: Translation.SC_BULK_APPLY_TRADING_ENTITY_DEFAULTS_CONFIRMATION,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      cancelButton: Translation.TC_BUTTON_NO,
      defaultButton: Translation.TC_BUTTON_YES
    }
    const dialogResult = await showDialog(dialogArgs)

    if (dialogResult !== Translation.TC_BUTTON_YES) {
      onClose?.()
      return
    }

    const [referenceDataEntities, entitiesToDelete] = getReferenceDataEntitiesForTradingEntityDefaultsAmend(
      tradingEntityMetaDetails,
      hideAliasField ? undefined : alias,
      primeBrokerId
    )
    if (referenceDataEntities.length > 0 || entitiesToDelete.length > 0) {
      onUpdateBulk(referenceDataEntities, entitiesToDelete)
    }
    onClose?.()
  }

  return (
    <ModalWindow title={Locale.translate(Translation.TC_BULK_APPLY_TRADING_ENTITY_DEFAULTS)}>
      <Content>
        <DialogField>
          <PrimeBrokerSelectboxField
            label={Locale.translate(Translation.TC_PRIME_BROKER)}
            fieldName={NegotiationFieldMeta.INITIATOR_PRIME_BROKER_ID}
            onUpdate={(value: string | null) => setPrimeBrokerId(value)}
            value={primeBrokerId}
          />
        </DialogField>
        {!hideAliasField && (
          <DialogField>
            <TextInputField
              label={Locale.translate(Translation.TC_ALIAS)}
              value={alias}
              fieldName={'alias'}
              onUpdate={(value: string | null) => setAlias(notEmpty(value) ? value : null)}
            />
          </DialogField>
        )}
      </Content>
      <ModalFooter>
        <TextButton type={TextButtonType.Tertiary} onClick={handleCancel}>
          {Locale.translate(Translation.TC_BUTTON_CANCEL)}
        </TextButton>
        <TextButton type={TextButtonType.Primary} onClick={handleApply}>
          {Locale.translate(Translation.TC_BUTTON_APPLY)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}

export const TradingEntityDefaultsDialog = withModal(connect(null, mapDispatchToProps)(TradingEntityDefaultsDialogComponent))
